import React, { Suspense } from 'react';

import Skeleton from './skeleton';

import { MultiSelectVehicleAssociationType } from '@cobliteam/common-types/src/vehicle';

import { ReactComponent as VehicleGroupIcon } from '@cobliteam/base-components/src/icons//placeholder-vehicle-group.svg';
import { ReactComponent as CarIcon } from '@cobliteam/base-components/src/icons/directions_car_cb.svg';
import { ReactComponent as FaceRecognitionIcon } from '@cobliteam/base-components/src/icons/directions_car_cb.svg';
import { ReactComponent as DriverCbIcon } from '@cobliteam/base-components/src/icons/driver-cb.svg';
import { ReactComponent as FilterIcon } from '@cobliteam/base-components/src/icons/filter_alt_cb.svg';
import { ReactComponent as HourglassIcon } from '@cobliteam/base-components/src/icons/hourglass.svg';
import { ReactComponent as LinkIcon } from '@cobliteam/base-components/src/icons/link.svg';
import { ReactComponent as SpeedIcon } from '@cobliteam/base-components/src/icons/speed.svg';

import { LeadingIcon } from '../../../../icons';
import LocationOnRoundedIcon from '@material-ui/icons/LocationOnRounded';
import PersonAddIcon from '@material-ui/icons/PersonAdd';

import type { IFiltersDef } from '../../types';

const SelectTimeServiceWindow = React.lazy(
  () => import('@cobliteam/ui/business/select/SelectTimeServiceWindow')
);
const SelectMaxStopTime = React.lazy(
  () => import('@cobliteam/ui/business/select/SelectMaxStopTime')
);
const MultiSelectVehicles = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectVehicles')
);
const MultiSelectDrivers = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectDrivers')
);
const MultiSelectDeviceStatus = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectDeviceStatus')
);
const MultiSelectGeofences = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectGeofences')
);
const MultiSelectUsers = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectUsers')
);
const MultiSelectWeekDay = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectWeekDay')
);
const MultiSelectDeviceType = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectDeviceType')
);
const MultiSelectVehicleAssociation = React.lazy(
  () =>
    import('@cobliteam/ui/business/multiselect/MultiSelectVehicleAssociation')
);
const MultiSelectVehicleGroups = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectVehicleGroups')
);
const MultiSelectRuleConditions = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectRuleConditions')
);
const MultiSelectFaceIdIdentificationMethods = React.lazy(
  () =>
    import(
      '@cobliteam/ui/business/multiselect/MultiSelectFaceIdIdentificationMethods'
    )
);
const MultiSelectMediaSeverity = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectMediaSeverity')
);
const MultiSelectMediaEventTrigger = React.lazy(
  () =>
    import('@cobliteam/ui/business/multiselect/MultiSelectMediaEventTrigger')
);
const MultiSelectMediaRequestType = React.lazy(
  () => import('@cobliteam/ui/business/multiselect/MultiSelectMediaRequestType')
);
const MultiSelectMediaRequestStatus = React.lazy(
  () =>
    import('@cobliteam/ui/business/multiselect/MultiSelectMediaRequestStatus')
);

const FiltersDef: IFiltersDef[] = [
  {
    key: 'MultiSelectDrivers',
    name: 'Motoristas',
    icon: <DriverCbIcon className='h-4 w-4' />,
    paramKey: 'driverIds',
    value: [],
    parseToParam: (value: []) => value.map((item: any) => item.id),
    parseToValue: (param) =>
      param.split(',').map((id: string) => ({ id, name: '' })),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectDrivers
          size='condensed'
          onChange={(value) =>
            onChangeFilter({ key: 'MultiSelectDrivers', value })
          }
          selectedDrivers={
            filters.find((f: any) => f.key === 'MultiSelectDrivers')?.value ??
            []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectDeviceStatus',
    name: 'Status do Dispositivo',
    icon: <FilterIcon className='h-4 w-4' />,
    paramKey: 'deviceStatusIds',
    value: [],
    parseToParam: (value: []) => value.map((item: any) => item),
    parseToValue: (param) => param.split(','),
    component: ({ onChangeFilter, filters, options }) => {
      const associationStatus = options?.associationStatus
        ? options?.associationStatus
        : MultiSelectVehicleAssociationType.NOT_ASSOCIATED_TO_VEHICLE;
      return (
        <Suspense fallback={<Skeleton />}>
          <MultiSelectDeviceStatus
            size='condensed'
            associationStatus={associationStatus}
            onChange={(value) =>
              onChangeFilter({
                key: 'MultiSelectDeviceStatus',
                value,
              })
            }
            selectedStatus={
              filters.find((f: any) => f.key === 'MultiSelectDeviceStatus')
                ?.value ?? new Set()
            }
          />
        </Suspense>
      );
    },
  },
  {
    key: 'MultiSelectUsers',
    name: 'Usuários',
    icon: <PersonAddIcon className='h-4 w-4' />,
    paramKey: 'usersIds',
    value: [],
    parseToParam: (value: []) => value.map((item: any) => item.id),
    parseToValue: (param) =>
      param.split(',').map((id: string) => ({ id, name: '' })),
    component: ({ onChangeFilter, filters, options }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectUsers
          size='condensed'
          title={options?.name}
          icon={options?.icon}
          onChange={(value) =>
            onChangeFilter({ key: 'MultiSelectUsers', value })
          }
          selectedUsersIds={
            filters
              .find((f: any) => f.key === 'MultiSelectUsers')
              ?.value.map((v: any) => v.id) ?? []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectGeofences',
    name: 'Locais',
    icon: <LocationOnRoundedIcon className='h-4 w-4' />,
    paramKey: 'geofenceIds',
    value: [],
    parseToParam: (value: []) => value.map((item: any) => item.id),
    parseToValue: (param) =>
      param.split(',').map((id: string) => ({ id, name: '' })),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectGeofences
          size='condensed'
          onChange={(value) =>
            onChangeFilter({ key: 'MultiSelectGeofences', value })
          }
          selectedGeofencesIds={
            filters
              .find((f: any) => f.key === 'MultiSelectGeofences')
              ?.value.map((v: any) => v.id) ?? []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectVehicleAssociation',
    name: 'Associação de Veículos',
    icon: <LinkIcon className='h-4 w-4' />,
    paramKey: 'vehicleAssociationIds',
    value: [],
    parseToParam: (value: []) => value.map((item: any) => item.id),
    parseToValue: (param) =>
      param.split(',').map((id: string) => ({ id, name: '' })),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectVehicleAssociation
          size='condensed'
          isOpen={false}
          onDropdownClick={() => null}
          onCancel={() => null}
          onChange={(value) =>
            onChangeFilter({
              key: 'MultiSelectVehicleAssociation',
              value,
            })
          }
          selectedAssociationStatus={
            filters.find((f: any) => f.key === 'MultiSelectVehicleAssociation')
              ?.value ?? new Set()
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectVehicleGroups',
    name: 'Grupos',
    icon: <VehicleGroupIcon className='h-4 w-4' />,
    paramKey: 'vehicleGroupsIds',
    value: [],
    parseToParam: (value: []) => value.map((item: any) => item.id),
    parseToValue: (param) =>
      param.split(',').map((id: string) => ({ id, name: '' })),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectVehicleGroups
          size='condensed'
          onChange={(value) =>
            onChangeFilter({
              key: 'MultiSelectVehicleGroups',
              value,
            })
          }
          selectedVehicleGroups={
            filters.find((f: any) => f.key === 'MultiSelectVehicleGroups')
              ?.value ?? []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectVehicles',
    name: 'Veículos',
    icon: <CarIcon className='h-4 w-4' />,
    paramKey: 'vehicleIds',
    value: [],
    parseToParam: (value: []) => value.map((item: any) => item.id),
    parseToValue: (param) =>
      param.split(',').map((id: string) => ({ id, name: '' })),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectVehicles
          size='condensed'
          onChange={(value) =>
            onChangeFilter({ key: 'MultiSelectVehicles', value })
          }
          selectedVehicles={
            filters.find((f: any) => f.key === 'MultiSelectVehicles')?.value ??
            []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectFaceIdIdentificationMethods',
    name: 'MultiSelectFaceIdIdentificationMethods',
    icon: <FaceRecognitionIcon className='h-4 w-4' />,
    paramKey: 'identificationMethods',
    value: [],
    parseToParam: (value: []) => value,
    parseToValue: (param: string) => param.split(','),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectFaceIdIdentificationMethods
          size='condensed'
          onChange={(value: any) =>
            onChangeFilter({
              key: 'MultiSelectFaceIdIdentificationMethods',
              value,
            })
          }
          selectedOptions={
            filters.find(
              (f: any) => f.key === 'MultiSelectFaceIdIdentificationMethods'
            )?.value ?? []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectMediaSeverity',
    name: 'MultiSelectMediaSeverity',
    icon: <SpeedIcon className='h-4 w-4' />,
    paramKey: 'severity',
    value: [],
    parseToParam: (value: []) => value,
    parseToValue: (param: string) => param.split(','),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectMediaSeverity
          size='condensed'
          onChange={(value: any) =>
            onChangeFilter({
              key: 'MultiSelectMediaSeverity',
              value,
            })
          }
          selectedOptions={
            filters.find((f: any) => f.key === 'MultiSelectMediaSeverity')
              ?.value ?? []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectMediaEventTrigger',
    name: 'MultiSelectMediaEventTrigger',
    icon: <SpeedIcon className='h-4 w-4' />,
    paramKey: 'eventTrigger',
    value: [],
    parseToParam: (value: []) => value,
    parseToValue: (param: string) => param.split(','),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectMediaEventTrigger
          size='condensed'
          onChange={(value: any) =>
            onChangeFilter({
              key: 'MultiSelectMediaEventTrigger',
              value,
            })
          }
          selectedOptions={
            filters.find((f: any) => f.key === 'MultiSelectMediaEventTrigger')
              ?.value ?? []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectMediaRequestType',
    name: 'MultiSelectMediaRequestType',
    icon: <SpeedIcon className='h-4 w-4' />,
    paramKey: 'requestType',
    value: [],
    parseToParam: (value: []) => value,
    parseToValue: (param: string) => param.split(','),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectMediaRequestType
          size='condensed'
          onChange={(value: any) =>
            onChangeFilter({
              key: 'MultiSelectMediaRequestType',
              value,
            })
          }
          selectedOptions={
            filters.find((f: any) => f.key === 'MultiSelectMediaRequestType')
              ?.value ?? []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectMediaRequestStatus',
    name: 'MultiSelectMediaRequestStatus',
    icon: <SpeedIcon className='h-4 w-4' />,
    paramKey: 'requestStatus',
    value: [],
    parseToParam: (value: []) => value,
    parseToValue: (param: string) => param.split(','),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectMediaRequestStatus
          size='condensed'
          onChange={(value: any) =>
            onChangeFilter({
              key: 'MultiSelectMediaRequestStatus',
              value,
            })
          }
          selectedOptions={
            filters.find((f: any) => f.key === 'MultiSelectMediaRequestStatus')
              ?.value ?? []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectDeviceType',
    name: 'Tipo de Dispositivo',
    icon: <LeadingIcon className='h-4 w-4' />,
    paramKey: 'deviceTypes',
    value: [],
    parseToParam: (value: []) => value.map((item: any) => item),
    parseToValue: (param) => param.split(','),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectDeviceType
          size='condensed'
          onChange={(value) =>
            onChangeFilter({ key: 'MultiSelectDeviceType', value })
          }
          selectedTypes={
            filters.find((f: any) => f.key === 'MultiSelectDeviceType')
              ?.value ?? []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectWeekDay',
    name: 'Dias da semana',
    icon: <LeadingIcon className='h-4 w-4' />,
    paramKey: 'weekDays',
    value: [],
    parseToParam: (value: []) => value,
    parseToValue: (param: string) => param.split(','),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectWeekDay
          size='condensed'
          onChange={(value) =>
            onChangeFilter({ key: 'MultiSelectWeekDay', value })
          }
          selectedDays={
            filters.find((f: any) => f.key === 'MultiSelectWeekDay')?.value ??
            []
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'MultiSelectRuleConditions',
    name: 'Condições',
    icon: <LeadingIcon className='h-4 w-4' />,
    paramKey: 'ruleConditionIds',
    value: [],
    parseToParam: (value: []) => value,
    parseToValue: (param) => param.split(','),
    component: ({ onChangeFilter, filters, options }) => (
      <Suspense fallback={<Skeleton />}>
        <MultiSelectRuleConditions
          size='condensed'
          onChange={(value) =>
            onChangeFilter({
              key: 'MultiSelectRuleConditions',
              value,
            })
          }
          selectedConditions={
            filters.find((f: any) => f.key === 'MultiSelectRuleConditions')
              ?.value ?? []
          }
          selectedRule={options?.selectedRule}
        />
      </Suspense>
    ),
  },
  {
    key: 'SelectTimeServiceWindow',
    name: 'SelectTimeServiceWindow',
    icon: <HourglassIcon className='h-4 w-4' />,
    paramKey: 'windowTimeService',
    value: [],
    parseToParam: (value: []) => value,
    parseToValue: (param: string) => param.split(','),
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <SelectTimeServiceWindow
          size='condensed'
          onChange={(value: any) =>
            onChangeFilter({
              key: 'SelectTimeServiceWindow',
              value,
            })
          }
          selectedHours={
            filters.find((f: any) => f.key === 'SelectTimeServiceWindow')
              ?.value ?? undefined
          }
        />
      </Suspense>
    ),
  },
  {
    key: 'SelectMaxStopTime',
    name: 'Tempo Máximo de Parada',
    icon: <HourglassIcon className='h-4 w-4' />,
    paramKey: 'maxStopTime',
    value: undefined,
    parseToParam: (value: number | undefined) =>
      value ? value.toString() : '',
    parseToValue: (param: string) => {
      const value = param.split(',').map(Number)[0];
      return value >= 5999 ? 5999 : value;
    },
    component: ({ onChangeFilter, filters }) => (
      <Suspense fallback={<Skeleton />}>
        <SelectMaxStopTime
          size='condensed'
          onChange={(value: any) =>
            onChangeFilter({ key: 'SelectMaxStopTime', value })
          }
          selectedMinutes={
            filters.find((f: any) => f.key === 'SelectMaxStopTime')?.value ??
            undefined
          }
        />
      </Suspense>
    ),
  },
];

export default FiltersDef;
